// src/hooks/api/useProfile.js
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { serverRequest } from '../../util/server_request';
import { notifyUser } from '../../util/util';

// Query Keys
export const profileKeys = {
  all: ['profile'],
  user: () => [...profileKeys.all, 'user'],
  profiles: () => [...profileKeys.all, 'profiles'],
};

// Fetch user profile
export const useProfile = () => {
  return useQuery({
    queryKey: profileKeys.user(),
    queryFn: async () => {
      const result = await serverRequest('GET', 'profile');
      return result.data;
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Fetch all employer profiles (for switching)
// Returns profiles with 'active' field indicating current active employer
export const useProfiles = () => {
  return useQuery({
    queryKey: profileKeys.profiles(),
    queryFn: async () => {
      const result = await serverRequest('GET', 'profile');
      return result.data || result;
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Switch employer profile
// Backend determines which employer is active based on the request
export const useSwitchProfile = () => {
  return useMutation({
    mutationFn: async (employerNumber) => {
      return await serverRequest('POST', 'profile/switch', { employerNumber });
    },
    onSuccess: () => {
      notifyUser('success', 'Switched employer successfully! Reloading...', 'Success');
      // Reload entire page to refresh all data for new employer context
      window.location.reload();
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Update profile
export const useUpdateProfile = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (profileData) => {
      return await serverRequest('PUT', 'profile', profileData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: profileKeys.all });
      notifyUser('success', 'Profile updated successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Change password
export const useChangePassword = () => {
  return useMutation({
    mutationFn: async (passwordData) => {
      return await serverRequest('POST', 'profile/change-password', passwordData);
    },
    onSuccess: () => {
      notifyUser('success', 'Password changed successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Change PIN
export const useChangePin = () => {
  return useMutation({
    mutationFn: async (pinData) => {
      return await serverRequest('POST', 'profile/change-pin', pinData);
    },
    onSuccess: () => {
      notifyUser('success', 'PIN changed successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};

// Reset PIN
export const useResetPin = () => {
  return useMutation({
    mutationFn: async (resetData) => {
      return await serverRequest('POST', 'profile/reset-pin', resetData);
    },
    onSuccess: () => {
      notifyUser('success', 'PIN reset successfully!', 'Success');
    },
    onError: (error) => {
      notifyUser('error', error, 'Error');
    },
  });
};
